import Api from 'services/Api'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'

import { Tier } from './accounts'

export interface TierDefinition {
  tierId: string
  displayName: string
  rank: number
  adminsCount: number | null
  storageQuotaBytes: number
  photoQuotaLimit: number | null
}

interface TiersResponse {
  tiers?: TierDefinition[]
}

export type TierPlanKey = '_free_tier' | 'tier1' | 'tier2' | 'tier3' | 'tier4'

const DEFAULT_TIER_DEFINITIONS: TierDefinition[] = [
  {
    tierId: 'tier0',
    displayName: 'GroupCal',
    rank: 0,
    adminsCount: 1,
    storageQuotaBytes: 5242880,
    photoQuotaLimit: 25
  },
  {
    tierId: 'tier1',
    displayName: 'GroupCal Pro',
    rank: 1,
    adminsCount: 1,
    storageQuotaBytes: 52428800,
    photoQuotaLimit: 150
  },
  {
    tierId: 'tier2',
    displayName: 'GroupCal Business',
    rank: 2,
    adminsCount: 3,
    storageQuotaBytes: 104857600,
    photoQuotaLimit: 500
  },
  {
    tierId: 'tier3',
    displayName: 'GroupCal Business+',
    rank: 3,
    adminsCount: null,
    storageQuotaBytes: 1048576000,
    photoQuotaLimit: 1500
  },
  {
    tierId: 'tier4',
    displayName: 'GroupCal Enterprise',
    rank: 4,
    adminsCount: null,
    storageQuotaBytes: 1048576000,
    photoQuotaLimit: null
  }
]

const PLAN_KEY_TO_TIER_ID: Record<TierPlanKey, string> = {
  _free_tier: 'tier0',
  tier1: 'tier1',
  tier2: 'tier2',
  tier3: 'tier3',
  tier4: 'tier4'
}

const TIER_TO_TIER_ID: Record<Tier, string> = {
  [Tier.FREE]: 'tier0',
  [Tier.PRO]: 'tier1',
  [Tier.BUSINESS]: 'tier2',
  [Tier.BUSINESS_PLUS]: 'tier3',
  [Tier.ENTERPRISE]: 'tier4'
}

const TIER_ID_TO_TIER: Record<string, Tier> = {
  tier0: Tier.FREE,
  tier1: Tier.PRO,
  tier2: Tier.BUSINESS,
  tier3: Tier.BUSINESS_PLUS,
  tier4: Tier.ENTERPRISE
}

function normalizeTierDefinition(
  tier: Partial<TierDefinition> | null | undefined
): TierDefinition | null {
  if (!tier?.tierId) return null

  return {
    tierId: tier.tierId,
    displayName: tier.displayName ?? tier.tierId,
    rank: Number.isFinite(tier.rank) ? Number(tier.rank) : 0,
    adminsCount:
      tier.adminsCount === null || tier.adminsCount === undefined
        ? null
        : Number(tier.adminsCount),
    storageQuotaBytes: Number(tier.storageQuotaBytes ?? 0),
    photoQuotaLimit:
      tier.photoQuotaLimit === null || tier.photoQuotaLimit === undefined
        ? null
        : Number(tier.photoQuotaLimit)
  }
}

function mergeTierDefinitions(tiers: TierDefinition[]): TierDefinition[] {
  const merged = new Map(
    DEFAULT_TIER_DEFINITIONS.map((tier) => [tier.tierId, tier])
  )

  tiers.forEach((tier) => {
    const existing = merged.get(tier.tierId)
    merged.set(tier.tierId, {
      ...(existing ?? tier),
      ...tier
    })
  })

  return Array.from(merged.values()).sort(
    (left, right) => left.rank - right.rank
  )
}

function parseCachedTierDefinitions(): TierDefinition[] | null {
  const cached = LocalStorage.get(LocalStorageKey.TIERS_CACHE)
  if (!cached) return null

  try {
    const parsed = JSON.parse(cached) as TiersResponse | TierDefinition[]
    const tiers = Array.isArray(parsed) ? parsed : parsed?.tiers
    if (!Array.isArray(tiers)) return null

    return mergeTierDefinitions(
      tiers
        .map((tier) => normalizeTierDefinition(tier))
        .filter((tier): tier is TierDefinition => tier != null)
    )
  } catch (error) {
    Logger.red('Failed to parse cached tiers', error)
    return null
  }
}

export function getTierDefinitions(): TierDefinition[] {
  return parseCachedTierDefinitions() ?? DEFAULT_TIER_DEFINITIONS
}

export async function fetchAndCacheTiers(): Promise<TierDefinition[]> {
  try {
    const response = await Api.get('/v1/tiers')
    const tiers = Array.isArray((response.data as TiersResponse)?.tiers)
      ? ((response.data as TiersResponse).tiers ?? [])
      : []

    const normalized = mergeTierDefinitions(
      tiers
        .map((tier) => normalizeTierDefinition(tier))
        .filter((tier): tier is TierDefinition => tier != null)
    )

    LocalStorage.set(
      LocalStorageKey.TIERS_CACHE,
      JSON.stringify({ tiers: normalized })
    )

    return normalized
  } catch (error) {
    Logger.red('Failed to fetch tiers', error)
    return getTierDefinitions()
  }
}

export function getTierDefinitionByPlanKey(
  planKey: TierPlanKey
): TierDefinition | undefined {
  const tierId = PLAN_KEY_TO_TIER_ID[planKey]
  return getTierDefinitions().find((tier) => tier.tierId === tierId)
}

export function getTierDefinitionByTier(
  tier: Tier
): TierDefinition | undefined {
  const tierId = TIER_TO_TIER_ID[tier]
  return getTierDefinitions().find((item) => item.tierId === tierId)
}

export function getAdminsCountByPlanKey(planKey: TierPlanKey): number | null {
  return getTierDefinitionByPlanKey(planKey)?.adminsCount ?? null
}

export function getPhotoQuotaLimitByPlanKey(
  planKey: TierPlanKey
): number | null {
  return getTierDefinitionByPlanKey(planKey)?.photoQuotaLimit ?? null
}

export function getAllowedAdminsForTier(tier: Tier): number {
  const adminsCount = getTierDefinitionByTier(tier)?.adminsCount
  return adminsCount == null ? Number.MAX_SAFE_INTEGER : adminsCount
}

export function getMinimumTierForAdminCount(amount = 0): Tier {
  const adminCount = Math.max(0, amount)
  const tier = getTierDefinitions().find((item) => {
    if (adminCount <= 0 && item.rank === 0) {
      return true
    }

    return item.adminsCount == null || item.adminsCount > adminCount
  })

  return TIER_ID_TO_TIER[tier?.tierId ?? 'tier0'] ?? Tier.FREE
}
